<template>
    <div
        class="z-video"
        @mouseover="handleMouseOver"
        @mouseout="handleMouseOut"
        :style="width ? `width: ${width}px` : null"
    >
        <div
            class="z-video__title"
            v-if="title"
            :class="{'z-video__title--visible': title && started && (paused || hovered)}"
        >
            <span v-html="title"></span>
        </div>
        <video
            ref="videoPlayer"
            class="video-js vjs-default-skin"
            :controls="controls"
            preload="auto"
        ></video>
    </div>
</template>

<script setup>
import {onBeforeUnmount, onMounted, ref, watch} from 'vue'
import videojs from 'video.js'

const props = defineProps({
    poster: {
        type: String
    },
    src: {
        type: [String, Array],
        required: true
    },
    muted: {
        type: Boolean,
        default: true
    },
    loop: {
        type: Boolean,
        default: false
    },
    autoplay: {
        type: Boolean,
        default: true
    },
    title: {
        type: String
    },
    width: {
        type: String
    },
    controls: {
        type: Boolean,
        default: true
    }
})

const player = ref(null)
const volume = ref(0.5)
const playing = ref(false)
const paused = ref(true)
const hovered = ref(null)
const started = ref(false)
const titleTimeout = ref(null)
const videoPlayer = ref(null)

const getVideoType = (src) => {
    if (!src || typeof src !== 'string') return null

    return src.match(/\.(mp4|webm|ogg)$/)?.[1] ?? null
}

const prepareSrc = (src) => {
    if (Array.isArray(src)) {
        return src.map(item => ({
            type: `video/${getVideoType(item)}`,
            src: item
        }))
    }

    const type = getVideoType(src)
    if (!type) return null

    return {
        type: `video/${type}`,
        src
    }
}

const playerInitialize = () => {
    player.value = videojs(
        videoPlayer.value,
        {
            aspectRatio: '16:9',
            poster: props.poster,
            techOrder: ['html5'],
            autoplay: props.autoplay,
            loop: props.loop,
            children: {
                posterImage: true,
                bigPlayButton: true,
                controlBar: {
                    children: {
                        playToggle: true,
                        currentTimeDisplay: true,
                        timeDivider: true,
                        remainingTimeDisplay: true,
                        progressControl: true,
                        volumePanel: {
                            inline: false
                        },
                        fullscreenToggle: true
                    }
                }
            }
        }
    )
}

const playerDispose = () => {
    if (player.value) {
        player.value.dispose()
    }
}

const playerSetSrc = (src) => {
    player.value.src(src)
}

const playerSetVolume = () => {
    if (props.muted) {
        player.value.volume(0)
    } else {
        player.value.volume(volume.value)
    }
}

const playerSetPoster = (url) => {
    player.value.poster(url)
}

const handlePlayerErrors = () => {
    const errorMessage = player.value.error().message
    console.log(errorMessage)
}

const handleVolumeChange = () => {
    if (!props.muted) {
        volume.value = player.value.volume()
    }
}

const handlePlay = () => {
    playing.value = true
    paused.value = false
    if (!started.value) started.value = true
}

const handlePause = () => {
    playing.value = false
    paused.value = true
}

const handleMouseOver = () => {
    hovered.value = true
    if (!titleTimeout.value) {
        titleTimeout.value = setTimeout(() => {
            hovered.value = false
        }, 2000)
    }
}

const handleMouseOut = () => {
    hovered.value = false
    clearTimeout(titleTimeout.value)
}

const playerSetupEvents = () => {
    player.value.on('error', handlePlayerErrors)
    player.value.on('volumechange', handleVolumeChange)
    player.value.on('play', handlePlay)
    player.value.on('pause', handlePause)
}

watch(props.src, (newValue) => {
    playerSetSrc(prepareSrc(newValue))
})

watch(props.muted, () => {
    playerSetVolume()
})

watch(props.poster, (newValue) => {
    playerSetPoster(newValue)
})

onMounted(() => {
    playerInitialize()
    playerSetSrc(prepareSrc(props.src))
    playerSetupEvents()
    playerSetVolume()

    if(props.autoplay){
        handlePlay()
    }
})

onBeforeUnmount(() => {
    playerDispose()
})

defineExpose({
    props
})
</script>

<style lang="scss" src="./index.scss"></style>
